import React, { useState } from "react";
import { Box, IconButton } from "@mui/joy";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Rating from "@mui/material/Rating";
import { GroupedActivityItem } from "../../store/activityStore";

interface PostcardPhotoCardProps {
  activity: GroupedActivityItem;
}

export const PostcardPhotoCard: React.FC<PostcardPhotoCardProps> = ({ activity }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const photos = activity.grouped_photos || [];
  const photoCount = photos.length;
  const hasMultiple = photoCount > 1;

  const placeName = activity.place?.name;
  const listTitle = activity.list?.title || "a list";
  const username = activity.profile?.username || activity.profile?.full_name || "Someone";
  const photoCountText = photoCount === 1 ? "1 photo uploaded" : `${photoCount} photos uploaded`;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photoCount - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < photoCount - 1 ? prev + 1 : 0));
  };

  return (
    <Box
      data-testid="postcard-photo-card"
      sx={{
        display: "flex",
        gap: "3px",
        transform: "rotate(-0.5deg)",
        borderRadius: "4px",
        overflow: "hidden",
        boxShadow: "0 3px 14px rgba(0,0,0,0.1)",
      }}
    >
      {/* Left side: Photo */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            width: "100%",
            aspectRatio: "3/4",
            position: "relative",
            backgroundColor: "#eee",
          }}
        >
          {photos.length > 0 && (
            <img
              src={photos[currentIndex]?.file_url}
              alt="Activity upload"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          )}

          {/* Photo counter badge */}
          {hasMultiple && (
            <Box
              data-testid="photo-counter"
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                background: "rgba(0,0,0,0.5)",
                color: "#fff",
                fontSize: "11px",
                padding: "3px 10px",
                borderRadius: "12px",
                backdropFilter: "blur(4px)",
              }}
            >
              {currentIndex + 1} / {photoCount}
            </Box>
          )}

          {/* Carousel arrows */}
          {hasMultiple && (
            <>
              <IconButton
                aria-label="Previous photo"
                onClick={handlePrev}
                sx={{
                  position: "absolute",
                  left: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 26,
                  height: 26,
                  minWidth: 26,
                  minHeight: 26,
                  backgroundColor: "rgba(255,255,255,0.8)",
                  borderRadius: "50%",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.95)" },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                aria-label="Next photo"
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 26,
                  height: 26,
                  minWidth: 26,
                  minHeight: 26,
                  backgroundColor: "rgba(255,255,255,0.8)",
                  borderRadius: "50%",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.95)" },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}

          {/* Dot indicators */}
          {hasMultiple && (
            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "5px",
              }}
            >
              {photos.map((photo, index) => (
                <Box
                  key={photo.id}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: index === currentIndex ? "#fff" : "rgba(255,255,255,0.4)",
                    transition: "background-color 0.2s",
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Right side: Postcard back */}
      <Box
        data-testid="postcard-back"
        sx={{
          flex: 1,
          backgroundColor: "#fffdf9",
          padding: "14px",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          aspectRatio: "3/4",
          borderLeft: "1px solid #e8e0d4",
        }}
      >
        {/* Stamp */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            width: 40,
            height: 46,
            border: "1.5px dashed #ccc",
            borderRadius: "2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,245,230,0.5)",
          }}
        >
          <span style={{ fontSize: "18px" }}>✈️</span>
        </Box>

        {/* Place name */}
        <Box
          sx={{
            fontFamily: "'Georgia', serif",
            fontSize: "17px",
            color: "#2a2a2a",
            fontWeight: 600,
            maxWidth: "65%",
            lineHeight: 1.3,
          }}
        >
          {placeName || "Unknown Place"}
        </Box>

        {/* Subtitle */}
        <Box
          sx={{
            fontSize: "10px",
            color: "#aaa",
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            marginTop: "3px",
          }}
        >
          {listTitle}
        </Box>

        {activity.place?.rating != null && activity.place.rating > 0 && (
          <Rating
            value={activity.place.rating}
            precision={0.5}
            readOnly
            size="small"
            sx={{ marginTop: "6px", opacity: 0.8 }}
          />
        )}

        {activity.place?.notes && (
          <Box
            sx={{
              marginTop: "8px",
              fontFamily: "'Georgia', serif",
              fontStyle: "italic",
              fontSize: "12px",
              color: "#888",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {activity.place.notes}
          </Box>
        )}

        {/* Photo count message */}
        <Box
          sx={{
            marginTop: "14px",
            fontFamily: "'Georgia', serif",
            fontStyle: "italic",
            fontSize: "12px",
            color: "#888",
            lineHeight: 1.5,
          }}
        >
          {photoCountText}
        </Box>

        {/* Vertical divider */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "55%",
            bottom: 12,
            borderLeft: "1px solid #e0d8ce",
          }}
        />

        {/* Address lines */}
        <Box sx={{ marginTop: "auto" }}>
          <Box sx={{ marginLeft: "52%" }}>
            <Box
              sx={{
                fontSize: "10px",
                color: "#bbb",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "6px",
              }}
            >
              To
            </Box>
            <Box
              sx={{
                borderBottom: "1px solid #e0d8ce",
                paddingBottom: "4px",
                marginBottom: "8px",
                fontSize: "12px",
                color: "#666",
                fontFamily: "'Georgia', serif",
              }}
            >
              {listTitle}
            </Box>
            <Box
              sx={{
                fontSize: "10px",
                color: "#bbb",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "6px",
              }}
            >
              From
            </Box>
            <Box
              sx={{
                borderBottom: "1px solid #e0d8ce",
                paddingBottom: "4px",
                fontSize: "12px",
                color: "#666",
                fontFamily: "'Georgia', serif",
              }}
            >
              {username}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
